import type {
  CustomerDetailsData,
  ExpenseDetailsData,
  HouseholdDetailsData,
  GetCustomerResource,
} from './customer';

export * from './customer';

export type ApplicationStep =
  | 'Plan Purpose'
  | 'Customer Details'
  | 'Household Expenses'
  | 'Bank Statements'
  | 'Customer Consent'
  | 'Plan Contract'
  | 'Payment'
  | 'Plan Result';

export enum ApplicationCompletedBy {
  CUSTOMER = 'Customer',
  MERCHANT = 'Merchant',
  ECOMMERCE = 'Ecommerce',
}

type PaymentFrequency = 'Weekly' | 'Fortnightly' | 'Monthly';

type PaymentCalculatorData = {
  /** Sale amount of the transaction */
  saleAmount: number;
  /** Percentage of deposit to pay */
  depositPercent: number;
  /** Percentage of deposit to pay */
  depositAmount?: number;
  /** Repayment every x amount of weeks */
  paymentFrequency: PaymentFrequency;
  /** Repayment over x number of months */
  paymentPeriod: number;
  /** Establishment fee */
  establishmentFee: number;
};

export enum NextStep {
  SUMMARY = 'summary',
  DEPOSIT_BUMP = 'deposit_bumping',
  BANK_STATEMENT = 'bank_statement',
  RUN_PRE_APPROVAL = 'run_pre_approval',
  DECLINED_MESSAGE = 'declined_message',
  CANCEL = 'cancel',
}

export enum CheckoutType {
  PRE_APPROVAL = 'pre-approval',
  STANDARD = 'standard',
  APPLY_AND_PAY = 'applyAndPay',
  UNDEFINED = '',
}

export enum CheckoutStep {
  CHECKOUT_STEP_PAYMENT = 'payment',
  CHECKOUT_STEP_PRE_APPROVAL_PAYMENT = 'pre-approval-payment',
  CHECKOUT_STEP_PENDING = 'pending',
  CHECKOUT_STEP_IN_PROGRESS = 'in_progress',
  CHECKOUT_STEP_PLAN_RESULT = 'plan_result',
  UNDEFINED = '',
}
// VERIFICATION CODE RESPONSE TYPE
export type VerifyPlanResponse = {
  planId: string;
  status: boolean;
};

// BSO Status
export type UpdateBsoOptionsData = {
  status: string;
  nextSteps: Array<NextStep>;
};

export enum PlanStatus {
  DECLINED = 'Declined',
  APPROVED = 'Approved',
  APPROVED_9_2 = 'Approved 9_2',
  APPROVED_SETTLED = 'Approved Settled',
  APPROVED_PENDING_ID = 'Approved_Pending_ID',
  APPROVED_STATEMENT_REQUESTED = 'Approved_Statement_Requested',
  REVIEW = 'Review',
  PENDING = 'Pending',
  ACTIVE = 'Active',
  CANCELLED = 'Cancelled',
  CLOSED = 'Closed',
  TERMINATED_CUSTOMER = 'Terminated Customer',
  TERMINATED_MERCHANT = 'Terminated Merchant',
  UNDEFINED = '',
}

export enum PlanApprovalCheckOutcome {
  APPROVED = 'Approved',
  REVIEW = 'Review',
  DECLINED = 'Declined',
  CANCELLED = 'Cancelled',
}

export enum PlanApprovalCheckMessageCode {
  BSO_VALIDITY_REVIEW = 'bso_not_valid',
}

export type PlanApprovalCheckResponse = {
  outcome: PlanApprovalCheckOutcome;
  messageCode: PlanApprovalCheckMessageCode;
};

export type PlanAction =
  | 'activate'
  | 'resend-details'
  | 'deposit-taker'
  | 'zero-deposit'
  | 'application-completer'
  | 'send-link-to-customer';

// PLAN APPROVAL RESPONSE TYPE
export type ApprovalResponse = {
  result: PlanStatus;
  nextStep: ApplicationStep;
};

export type CheckoutCancelledResponse = {
  status: 'Cancelled';
};

export type CancelledResponse = {
  cancelledStatus: PlanStatus;
};

export type CreateCheckoutParams = {
  attributes: {
    saleAmount: number;
    terms: number | string;
    depositPaid?: number | string | null;
    depositPaidPercentage: number | string;
    paymentPeriod: number | string;
    minimumDepositPercentage?: number | null;
    type: string;
    redirectUrl: string;
    expiresAt?: string;
  };
  merchantReference?: string;
};

export type CheckOutAttributes = {
  depositPaidPercentage: number;
  depositPayable: number;
  paymentPeriod: number;
  saleAmount: number;
  terms: string;
  paymentDetails: PaymentCalculatorData;
  type: CheckoutType;
  redirectUrl: string;
  expiresAt?: string;
  step?: CheckoutStep;
  platform?: string | null;
  showAllAvailableTerms?: boolean;
  planPreApprovalDetails?: PlanPreApprovalDetailsData;
  purposeOfLoan?: string;
};

export type PlanPreApprovalDetailsData = {
  status: string;
  nextSteps: Array<NextStep>;
  preApprovalOutCome: number;
  approvedDepositAmount: 0;
  preApprovalTermOutcome: string;
  approvedDepositPercentage: number;
};

export enum CheckoutStatus {
  APPROVED = 'approved',
  PENDING = 'pending',
  ACTIVE = 'active',
  REVIEW = 'review',
  DECLINED = 'declined',
  COMPLETE = 'complete',
  CANCELLED = 'cancelled',
  CLOSED = 'closed',
  APPROVED_PRE_APPROVAL_CARD_DETAILS_TAKEN = 'approved_pre_approval_card_details_taken',
  UNDEFINED = '',
}

export type CheckOutRequest = {
  attributes: {
    saleAmount: number;
    terms: number | string;
    depositPaid?: number | string | null;
    depositPaidPercentage: number | string;
    paymentPeriod: number | string;
    minimumDepositPercentage?: number | null;
  };
  merchantReference?: string;
};

export type CheckoutSuccessResponse = {
  id?: string;
  merchantReference?: string;
  status?: CheckoutStatus;
  createdAt?: string;
  expiresAt?: string | null;
  link?: string;
  customerId?: string | null;
  customerNumber?: string | null;
  planId?: string | null;
  planNumber?: string | null;
  nextSteps?: Array<NextStep>;
  attributes?: CheckOutAttributes;
  /**
   * Todo:
   * Change approvalStatus to planStatus?
   * May require a bit of thinking/investigation on the best way
   * to represent the 2 statuses in the FE
   */
  approvalStatus?: PlanStatus;
  applicationCompletedBy?: ApplicationCompletedBy;
  preApprovalDetails?: PreApprovalStatus;
  redirectUrl?: string;
  redirectEndpoint?: string;
  contactId?: string;
};

export type CheckoutItems = Array<{
  originalPrice: number | '-';
  discountedPrice: number | '-';
  productDescription: string;
  productImage: string;
  reference: string;
  productTitle: string;
  orderReceiptNumber: string;
  orderDate: string;
  quantity: number;
}>;

export type PreApprovalStatus = {
  status: string;
};

// TODO: Merge CheckoutSuccessResponse with CheckoutResource. They are essentially the same thing
export type CheckoutResource = {
  id: string;
  merchantReference: string;
  applicationCompletedBy: ApplicationCompletedBy;
  status?: CheckoutStatus;
  attributes: CheckOutAttributes;
  nextSteps?: Array<NextStep>;
  planId: string | null;
  planNumber?: string | null;
  customerId: string | null;
  customerNumber?: string | null;
  createdAt: string; // Fix this
  expiresAt: string | null;
  link?: string;
  approvalStatus?: PlanStatus;
};

//export type CheckoutSuccessResponse = CheckoutResource;
export type CheckoutUpdateSuccessResponse = CheckoutResource;

// CURRENT CHECKOUT RESPONSE TYPE
export type CurrentCheckoutResponse = CheckoutResource;

export type RatesSuccessResponse = {
  rates: Array<{
    minimumSaleAmount: number;
    minimumDepositPercentage: number;
    term: number;
    minimumPurchase: number;
    maximumPurchase: number;
    rate00: number | null;
    rate05: number | null;
    rate10: number | null;
    rate15: number | null;
    rate20: number | null;
    rate25: number | null;
    rate30: number | null;
    rate35: number | null;
    rate40: number | null;
    rate45: number | null;
    rate50: number | null;
  }>;
  establishmentFees: Array<{
    initialEstFee: number;
    repeatEstFee: number;
    term: number;
  }>;
  otherFees: {
    monthlyAccountKeepingFee: number;
    paymentProcessingFee: number;
  };
};

export type StoreSuccessResponse = {
  id: string;
  name: string;
  merchantId: string;
  merchantName: string;
  email: string | null;
  defaultTerm: number;
  depositTaker: boolean;
  fundingSource: string;
  applicationCompleter: boolean;
  storeNumber: string;
  storeStatus: string;
  capacityOffsetOn: boolean;
  customerLedOnly: boolean;
  bsoOffsetValue: number;
  storeDescription: string;
  paymentsProcessedBySugar?: boolean;
  merchantIndustry: string;
  marketSegment: string;
  showMobileCheckout: boolean;
};

export type PreApprovalCheckResponse = {
  nextStep: ApplicationStep;
};

export type AcceptDepositBumpingSuccessResponse = {
  nextSteps: Array<NextStep>;
  depositPaid: number;
  depositPaidPercentage: number;
};

export type PlansSuccessResponse = Array<{
  id: string;
  name: string;
  customerName: string;
  status: string;
  dateCreated: Date;
  merchantReference: string;
  staffName: string;
  depositPaymentStatus: string;
  saleAmount: string;
  depositPaid: string;
  amountSettledToDate: string;
  totalCreditAmount: string;
  msf: string;
  depositPaidPercentage: string;
  terms: string;
  contactId: string;
  finalSettlementDate: string;
  planBlockActivation: boolean;
  merchantSettled: string;
  creditAmount: string;
}>;

export type CustomerSearchResponse = {
  customerPartial: { id: string; phoneMobile: string } | null;
  errors: Array<{ code: number; message: string }>;
  isExistingCustomer: boolean;
  canCustomerProceed: boolean;
};

export type CustomerSearchParams = Required<
  Pick<CustomerDetailsData, 'firstName' | 'lastName' | 'middleName' | 'dateOfBirth'>
> &
  Partial<Pick<CustomerDetailsData, 'email' | 'phone'>>;

export type SubmitHouseholdExpensesRequest = {
  expenseDetails?: ExpenseDetailsData;
  householdDetails: HouseholdDetailsData;
};

export type SubmitHouseholdExpensesResponse = {
  customer: GetCustomerResource;
  nextStep: ApplicationStep;
};

export type PaymentMethodsResponse = Array<{
  id: string;
  name: string;
  paymentMethodType: string;
  cardProvider: string;
  cardNumber: string;
  fundingSource: string;
  cardExpiry: string;
}>;

export type MakePaymentResponse = {
  status: string;
  message: string;
  responseCode: string;
  transactionId: number;
};

export type MakePaymentData = {
  customerId: string;
  paymentType: string;
  planId: string;
};

export type UpdateCustomerData = {
  checkoutId: string;
  paymentDetails: {
    paymentStartDate: string;
  };
};

export type PaymentGatewayDetails = {
  serviceProvider: 'Eway' | 'Cloud_Payments';
  apiUrl: string;
  paymentCurrency: string;
  iframeUrl: string;
};

export type CloudCheckRequest = {
  payment_provider: string;
  checkoutId: string;
  token: string | null;
  card_holder: string | null;
  country?: string | null;
  cardNumber: string | null;
  card_expiry: string | null;
  card_type: string | null;
};

export type CustomerLivingExpeseIncomeData = {
  id: string;
  originalTotalMonthlyIncome: number;
  appliedLivingExpensePercentage: number;
  appliedFinancialExpensePercentage: number;
  groceriesMonthly: number;
  utility: number;
  accommodation: number;
  transportMonthlyCost: number;
  personalExpense: number;
  leisureEntertainmentMonthly: number;
  education: number;
  otherExpense: number;
  otherIncome?: number;
  noIfJointAccount: number;
  wages?: Array<{ key: string; value: string }>;
  creditCardRepayment: number;
  totalCreditLimit: number;
};

export type CustomerCreditAccountsData = Array<{
  id: string;
  creditor: string;
  name: string;
  productType: string;
}>;

export type CustomerIncomeExpenseData = {
  approval_data?: CustomerLivingExpeseIncomeData;
  credit_accounts?: CustomerCreditAccountsData;
};

export type CustomerDeclaredIncomeExpenseData = {
  declaredTotalMonthlyLivingExpenses: number;
  declaredTotalMonthlyFinancialExpenses: number;
  declaredTotalMonthlyIncome: number;
  /** Optional: only set this value if the customer has joint accounts */
  appliedLivingExpensePercentage?: number;
  /** Optional: only set this value if the customer has joint accounts */
  appliedFinancialExpensePercentage?: number;
};

export type PlanResponse = {
  id: string;
  name: string;
  contactId: string;
  customerId: string;
  customerNumber: string;
  customerName: string;
  saleAmount: number;
  status: PlanStatus;
  depositPaymentStatus: string;
  dateCreated: string;
  merchantReference: string;
  numberOfRepayments: number;
  establishmentFee: number;
  accountKeepingFee: number;
  repaymentFrequency: string;
  loanAmountPerPayment: number;
  repaymentAmount: number;
  depositPaid: number;
  totalCreditAmount: number;
  terms: number;
  amountSettledToDate: number;
  msf: number;
  depositPaidPercentage: number;
  validationCode: string;
  finalSettlementDate: string;
  planBlockActivation: boolean;
  creditAmount: number;
  merchantSettled: string;
  applicationCompletedBy: ApplicationCompletedBy;
  checkoutId: string;
  loanAmount: number;
  planType: string;
  storeId: string;
  siftStatus: string;
  businessDecisionBeforeSift: string;
  loanPurpose: string;
  consentApprovalDate: string;
  paymentProcessingFee: number;
  assessmentTier: string;
  applicationStep: ApplicationStep;
  withinAvailableCredit: boolean;
};
